/**
 * The changed lines of a set of files, the words that changed highlighted:
 * one view for the Activity's code changes and a draft's What changed
 * (FRONT-3372). Lines render as plain text, never Markdown or HTML.
 */
import { changeSegments, type ChangeSegment } from '~/features/canvas/changes-diff';
import type { ChangedFile } from '~/hooks/queries/use-app-activity';

/** Pure: one changed part of a file as the words that changed, or null when the diff gave up. */
export function hunkSegments(hunk: { before: string; after: string }): ChangeSegment[] | null {
  return changeSegments(hunk.before, hunk.after);
}

const STATUS_WORD: Record<ChangedFile['status'], string> = { added: 'Added', changed: 'Changed', removed: 'Removed' };

/** Pure: the one sentence a file without readable lines gets in their place. */
export function fileNoteText(note: NonNullable<ChangedFile['note']>): string {
  if (note === 'binary') return 'Not a text file.';
  if (note === 'not shown') return 'Not shown: there are too many changes to show every file here.';
  return 'Too large to show here.';
}

export const TRUNCATED_LINE = 'Showing the first part of this file; the rest is too large to show here.';

/** Pure: whether any changed file's lines were left out, in whole or in part. */
export function leavesFilesOut(files: ChangedFile[]): boolean {
  return files.some((f) => f.note === 'not shown' || f.truncated === true);
}

/** Pure: the line above the list naming every file whose lines were left out, or null when none were. */
export function notShownLine(files: ChangedFile[]): string | null {
  const left = files.filter((f) => f.note === 'not shown').map((f) => f.path);
  if (left.length === 0) return null;
  return `${left.length} changed ${left.length === 1 ? 'file is' : 'files are'} not shown here: ${left.join(', ')}.`;
}

function Segments({ segments }: { segments: ChangeSegment[] }) {
  return (
    <>
      {segments.map((s, i) =>
        s.kind === 'same' ? (
          <span key={i}>{s.text}</span>
        ) : s.kind === 'removed' ? (
          <del key={i} className="bg-destructive/15 text-destructive no-underline line-through">
            {s.text}
          </del>
        ) : (
          <ins key={i} className="bg-success/15 text-success no-underline">
            {s.text}
          </ins>
        ),
      )}
    </>
  );
}

export function ChangedFilesList({ files }: { files: ChangedFile[] }) {
  if (files.length === 0) return <p className="text-xs text-muted-foreground">No files changed.</p>;
  const notShown = notShownLine(files);
  return (
    <div className="mt-1.5 space-y-2">
      {notShown && (
        // Above the list, so the owner can't miss that part of the draft isn't here.
        <p role="note" className="text-xs text-amber-600 dark:text-amber-300" data-testid="changed-files-not-shown">
          {notShown}
        </p>
      )}
    <ul className="space-y-2" data-testid="changed-files">
      {files.map((f) => (
        <li key={f.path} className="space-y-1" data-testid="changed-file">
          <p className="flex items-center gap-2 text-xs">
            <span className="w-16 shrink-0 text-muted-foreground">{STATUS_WORD[f.status]}</span>
            <span className="min-w-0 truncate font-mono">{f.path}</span>
          </p>
          {f.note ? (
            <p className="text-xs text-muted-foreground" data-testid="changed-file-note">{fileNoteText(f.note)}</p>
          ) : (
            f.hunks.map((h, i) => {
              const segments = hunkSegments(h);
              return (
                <pre key={i} className="max-h-64 overflow-auto whitespace-pre-wrap rounded border border-border bg-muted/30 p-2 font-mono text-[11px]">
                  {segments ? <Segments segments={segments} /> : 'Too many changes to show the words that changed.'}
                </pre>
              );
            })
          )}
          {f.truncated && <p className="text-xs text-muted-foreground" data-testid="changed-file-truncated">{TRUNCATED_LINE}</p>}
        </li>
      ))}
    </ul>
    </div>
  );
}
